import React, { useMemo } from 'react';

import RenderListItems from './RenderListItems';

const valueOrEmpty = value => (
    value === undefined || value === null || value === '' ? 'Не указано' : value
);

const getFullName = user => (
    [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() ||
    user?.email ||
    'Не указано'
);

const formatDateTime = value => {
    if (!value) return 'Не указано';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);

    return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
};

const getVariantLabel = variant => {
    switch (variant) {
        case 'personal':
            return 'Личные данные';
        case 'contact':
            return 'Контактные данные';
        case 'login':
            return 'Email';
        case 'password':
            return 'Пароль';
        default:
            return 'Профиль';
    }
};

const getSourceLabel = source => {
    switch (source) {
        case 'mobile':
            return 'MOBILE';
        case 'admin':
            return 'ADMIN';
        case 'system':
            return 'Система';
        case 'legacy':
            return 'Старый кабинет';
        default:
            return 'WEB';
    }
};

const makeLegacyChange = (field, label, before, after) => ({
    field,
    label,
    before: valueOrEmpty(before),
    after: valueOrEmpty(after),
});

const buildLegacyChanges = item => {
    const previous = item?.previousData || {};
    const user = item?.userObj || {};

    switch (item?.variant) {
        case 'personal':
            return [
                makeLegacyChange('firstName', 'Имя', previous.firstName ?? user.firstName, item.firstName),
                makeLegacyChange('lastName', 'Фамилия', previous.lastName ?? user.lastName, item.lastName),
                makeLegacyChange('gender', 'Пол', previous.gender ?? user.gender, item.gender),
                makeLegacyChange('birthData', 'Дата рождения', previous.birthData ?? user.birthData, item.birthData),
                makeLegacyChange('regionName', 'Регион', previous.regionName ?? user.regionName, item.region?.name),
            ].filter(change => String(change.before ?? '') !== String(change.after ?? ''));
        case 'contact':
            return [
                makeLegacyChange(
                    'contactNumber',
                    'Основной телефон',
                    previous.contactNumber ?? user.contactNumber,
                    item.contactNumber
                ),
                makeLegacyChange(
                    'backupNumber',
                    'Резервный телефон',
                    previous.backupNumber ?? user.backupNumber,
                    item.backupNumber
                ),
            ].filter(change => String(change.before ?? '') !== String(change.after ?? ''));
        case 'login':
            return [
                makeLegacyChange('email', 'Email', previous.email ?? user.email, item.email),
            ].filter(change => String(change.before ?? '') !== String(change.after ?? ''));
        case 'password':
            return [
                makeLegacyChange('password', 'Пароль', 'Скрыто', 'Изменён'),
            ];
        default:
            return [];
    }
};

const DetailsContent = ({ item }) => {
    const changes = useMemo(() => {
        if (Array.isArray(item?.changes) && item.changes.length) {
            return item.changes;
        }

        return buildLegacyChanges(item);
    }, [item]);

    if (!item?._id) return null;

    return (
        <div className="applicationChangeProfile__details">
            <div className="applicationChangeProfile__detailsTop">
                <div>
                    <div className="applicationChangeProfile__eyebrow">
                        {item.applicationNumber || 'История профиля'}
                    </div>
                    <h3>{getVariantLabel(item.variant)}</h3>
                    <p>
                        {item.audit
                            ? 'Изменение применено пользователем или системой без ручного подтверждения администратора.'
                            : 'Legacy-запись старой системы заявок.'}
                    </p>
                </div>

                <span className={`applicationChangeProfile__auditBadge ${item.audit ? 'success' : 'legacy'}`}>
                    {item.audit ? 'Применено' : 'Legacy'}
                </span>
            </div>

            <div className="applicationChangeProfile__infoGrid">
                <div className="applicationChangeProfile__infoCard">
                    <span>Пользователь</span>
                    <strong>{getFullName(item.userObj)}</strong>
                </div>
                <div className="applicationChangeProfile__infoCard">
                    <span>Email</span>
                    <strong>{valueOrEmpty(item.userObj?.email)}</strong>
                </div>
                <div className="applicationChangeProfile__infoCard">
                    <span>Источник</span>
                    <strong>{getSourceLabel(item.audit ? item.source : "legacy")}</strong>
                </div>
                <div className="applicationChangeProfile__infoCard">
                    <span>Кто изменил</span>
                    <strong>
                        {item.actorId
                            ? getFullName(item.actorObj || item.userObj)
                            : 'Legacy'}
                    </strong>
                </div>
                <div className="applicationChangeProfile__infoCard">
                    <span>Actor / роль</span>
                    <strong>
                        {item.actorId
                            ? `${item.actorRole || 'user'} · ${item.actorObj?.email || item.actorId}`
                            : 'Старая запись'}
                    </strong>
                </div>
                <div className="applicationChangeProfile__infoCard">
                    <span>Время</span>
                    <strong>{formatDateTime(item.appliedAt || item.createdAt)}</strong>
                </div>
            </div>

            <div className="applicationChangeProfile__changesHeader">
                <div>
                    <div className="applicationChangeProfile__eyebrow">Изменения</div>
                    <h4>Было → Стало</h4>
                </div>
                <span>{changes.length} полей</span>
            </div>

            <RenderListItems items={changes} />

            {item.variant === 'password' ? (
                <div className="applicationChangeProfile__securityNotice">
                    Значения паролей в аудит не записываются. Фиксируется только сам факт смены.
                </div>
            ) : null}
        </div>
    );
};

export default DetailsContent;
